/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useEffect, useRef, useState } from 'react'

import { useDashboard } from '@/hooks'

import { useColdStorage } from './ColdStorageContext'
import { type Units, sensorStatus } from './format'
import { useImageSrc } from './hooks'
import { PLACEHOLDER_ASPECT, type Size, fitBox, markerSizeFor, surfaceAssets, surfaceMarkers } from './layout'
import { GatewayMarker } from './markers/GatewayMarker'
import { SensorMarker } from './markers/SensorMarker'
import type { ImageMeta, InStoreAsset, MarkerRef, Position } from './types'
import { useDragMarkers } from './useDragMarkers'

export interface FloorPlanViewProps {
  /** The location whose surface is shown; undefined until one is picked. */
  selected?: InStoreAsset
  /** Its floor plan, if it has one; the signed URL arrives separately. */
  image?: ImageMeta
  imageSrc?: string
  /** Every location on this surface (those sharing the floor plan). */
  assets: InStoreAsset[]
  draft: Record<string, Position>
  units: Units
  editMode: boolean
  /** Grid pitch in px that drags snap to; 0 for free placement. */
  snapGrid?: number
  selectedSensorId?: number
  /** The sensor hovered here or in the list; its label shows in place of the selected one's. */
  hoveredSensorId?: number
  onSelectSensor: (id: number) => void
  onHoverSensor?: (id: number | undefined) => void
  onDrag: (marker: MarkerRef, position: Position) => void
}

/**
 * The right-hand pane: the floor plan image, or a dashed box when the location has
 * none, with a marker for every sensor and gateway on it. Positions are fractions of
 * the surface so the same layout fits any pane size. In edit mode the markers drag.
 */
export const FloorPlanView = ({
  selected,
  image,
  imageSrc,
  assets,
  draft,
  units,
  editMode,
  snapGrid = 0,
  selectedSensorId,
  hoveredSensorId,
  onSelectSensor,
  onHoverSensor,
  onDrag,
}: FloorPlanViewProps) => {
  const surfaceRef = useRef<HTMLDivElement>(null)
  const { bind } = useDragMarkers(surfaceRef, editMode, onDrag, editMode ? snapGrid : 0)
  const showGrid = editMode && snapGrid > 0
  // A spinner holds the surface's size until the browser has the bytes: an <img> that
  // is still loading has no height, and the markers would pile up at the top of a
  // collapsed surface until it arrived (only noticeable before the image is cached).
  const [loaded, setLoaded] = useState(false)
  const [natural, setNatural] = useState<Size>()
  useEffect(() => {
    setLoaded(false)
    setNatural(undefined)
  }, [imageSrc])

  // The surface is sized here, not by CSS: the largest box of the image's shape that
  // fits the pane, capped at the image's native size. Sizing both the surface and the
  // image to it keeps the markers, placed as fractions of the surface, on the image at
  // every pane size; letting the image overflow a pane-wide surface put them off it.
  const paneRef = useRef<HTMLDivElement>(null)
  const [pane, setPane] = useState<Size>({ width: 0, height: 0 })
  useEffect(() => {
    const el = paneRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const measure = () => setPane({ width: el.clientWidth, height: el.clientHeight })
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [selected?.id])
  const aspect = natural ? natural.width / natural.height : PLACEHOLDER_ASPECT
  const box = fitBox(pane, aspect, natural)
  const sized = box.width > 0
  const markerSize = markerSizeFor(box.width)

  if (!selected) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-base-content/60">
        Click a location to see its floor plan.
      </div>
    )
  }

  const markers = surfaceMarkers(assets, draft)
  const shared = assets.length > 1

  return (
    <div className="h-full w-full p-4 flex flex-col min-h-0">
      <div className="text-xs text-base-content/60 mb-2">
        <span className="truncate">
          {selected.name}
          {shared &&
            ` · floor plan shared with ${assets
              .filter((a) => a.id !== selected.id)
              .map((a) => a.name)
              .join(', ')}`}
          {!image && ' · no floor plan uploaded'}
        </span>
      </div>
      <div ref={paneRef} className="flex-1 min-h-0 flex items-start justify-center overflow-hidden">
        <div
          ref={surfaceRef}
          data-testid="floor-plan-surface"
          style={sized ? { width: box.width, height: box.height } : undefined}
          className={`relative select-none ${image ? '' : 'rounded-lg border-2 border-dashed border-base-300 bg-base-200/40'}`}
        >
          {image && !loaded && (
            <div data-testid="floor-plan-loading" className="w-full h-full flex items-center justify-center">
              <span className="loading loading-spinner loading-lg text-primary" aria-label="Loading floor plan" />
            </div>
          )}
          {image && imageSrc && (
            <img
              src={imageSrc}
              alt={`${selected.name} floor plan`}
              draggable={false}
              onLoad={(e) => {
                const { naturalWidth, naturalHeight } = e.currentTarget
                if (naturalWidth > 0 && naturalHeight > 0) setNatural({ width: naturalWidth, height: naturalHeight })
                setLoaded(true)
              }}
              className={`block w-full h-full ${loaded ? '' : 'absolute inset-0 opacity-0'}`}
            />
          )}
          {showGrid && (
            <div
              data-testid="floor-plan-grid"
              aria-hidden
              className="pointer-events-none absolute inset-0 z-[5]"
              style={{
                backgroundImage:
                  'linear-gradient(to right, rgba(8,60,138,0.18) 1px, transparent 1px), linear-gradient(to bottom, rgba(8,60,138,0.18) 1px, transparent 1px)',
                backgroundSize: `${snapGrid}px ${snapGrid}px`,
              }}
            />
          )}
          {(!image || loaded) &&
            markers.map((marker) =>
              marker.sensor ? (
                <SensorMarker
                  key={marker.key}
                  sensor={marker.sensor}
                  locationName={marker.asset.name}
                  status={sensorStatus(marker.sensor, marker.asset.threshold)}
                  position={marker.position}
                  size={markerSize}
                  units={units}
                  selected={marker.sensor.id === selectedSensorId}
                  showLabel={
                    hoveredSensorId == null
                      ? marker.sensor.id === selectedSensorId
                      : marker.sensor.id === hoveredSensorId
                  }
                  editMode={editMode}
                  onClick={() => onSelectSensor(marker.sensor!.id)}
                  onHoverChange={(hovering) => onHoverSensor?.(hovering ? marker.sensor!.id : undefined)}
                  handlers={bind(marker.ref)}
                />
              ) : (
                <GatewayMarker
                  key={marker.key}
                  gateway={marker.asset.gateway!}
                  locationName={marker.asset.name}
                  position={marker.position}
                  size={markerSize}
                  editMode={editMode}
                  handlers={bind(marker.ref)}
                />
              )
            )}
        </div>
      </div>
    </div>
  )
}

export const FloorPlan = () => {
  const context = useColdStorage()
  const units = useDashboard().configuration.units as Units
  const selected = context.assets.find((asset) => asset.id === context.surfaceLocationId)
  const image = selected ? context.imageFor(selected.id) : undefined
  const src = useImageSrc(image?.id)
  return (
    <FloorPlanView
      selected={selected}
      image={image}
      imageSrc={src.data}
      assets={surfaceAssets(selected, context.assets, context.imageFor)}
      draft={context.draft}
      units={units}
      editMode={context.editMode}
      snapGrid={context.snapGrid}
      selectedSensorId={context.selectedSensorId}
      hoveredSensorId={context.hoveredSensorId}
      onSelectSensor={context.selectSensor}
      onHoverSensor={context.setHoveredSensorId}
      onDrag={context.setDraftPosition}
    />
  )
}
